import React, { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../../../utils/styles';

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

interface IProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  error?: boolean;
  placeholder?: string;
}

function EmailTagsInput({ emails, onChange, error, placeholder = 'Enter email addresses' }: IProps) {
  const [input, setInput] = useState('');

  const add = (value: string) => {
    const email = value.trim();

    if (!email || !isValidEmail(email) || emails.includes(email)) return;
    onChange([...emails, email]);
  };

  const remove = (email: string) => {
    onChange(emails.filter(e => e !== email));
  };

  const handleChange = (value: string) => {
    if (value.includes(',')) {
      const parts = value.split(',');

      parts.slice(0, -1).forEach(add);

      setInput(parts[parts.length - 1] ?? '');
    } else {
      setInput(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add(input);
      setInput('');
    }
    if (e.key === 'Backspace' && !input && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap gap-1.5 rounded-md px-2 py-1.5 cursor-text border min-w-[120px] flex-1 border-gray-400 focus-within:ring-2 focus-within:ring-primary',
        {
          'border-red-500 focus-within:ring-2 focus-within:ring-red-500': error,
        },
      )}
    >
      {emails.map(email => (
        <div key={email} className="bg-blue-100 px-2 py-0.5 rounded flex items-center">
          {email}
          <X className="w-4 h-4 ml-1.5 border-none bg-transparent cursor-pointer" onClick={() => remove(email)} />
        </div>
      ))}
      <input
        value={input}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className="border-none outline-none flex-1 min-w-[180px] bg-transparent"
        placeholder={placeholder}
      />
    </div>
  );
}

export default EmailTagsInput;
